import { CREATE_TASK_CHAT_PURPOSE } from '@zerohuman/shared'
import type { ChatEntry } from '@zerohuman/ui'
import { type ComputedRef, computed, onUnmounted, type Ref, ref } from 'vue'
import {
    api,
    type ChatDetail,
    type ChatMessageRow,
    type ChatToolCallDecision,
    decisionRefusal,
    type GateDecision,
    type Me,
    refusalCode,
} from '@/api'
import { useChatToolCalls } from '@/composables/useChatToolCalls'
import { useUiMode } from '@/composables/useUiMode'
import { type AiAccountStatus, readAiAccountStatus, requireAiAccount } from '@/lib/aiAccountGuard'
import { type ChatToolItem, threadEntries, toolPayload, toolRunId } from '@/lib/chat'
import { memberLabel } from '@/lib/memberLabel'
import { grantWrites } from '@/lib/navGrant'
import { toolActionLabel } from '@/lib/presentation'

/**
 * Why Create task, in Standard, shows the form rather than the conversation with the COO (#244): nobody holds the COO
 * role, the enterprise's AI account is not connected, the person may not chat or may not decide what the conversation
 * asks them, or the conversation would not open for any other reason. Falling back never changes the account's mode.
 */
export type CreateTaskFallback = 'no_coo' | 'no_ai_account' | 'may_not_chat' | 'may_not_decide' | 'failed'

/** The one line each fallback says above the form. `no_coo` is followed by a link to Members, where the role is given. */
export const CREATE_TASK_FALLBACK_LINES: Readonly<Record<CreateTaskFallback, string>> = {
    no_coo: 'Nobody holds the COO role yet, so there is nobody to talk this task through with. Here is the form instead.',
    no_ai_account:
        'Your AI account is not connected, so the COO cannot talk this task through with you. Here is the form instead.',
    may_not_chat:
        'You may not chat in this company, so the COO cannot talk this task through with you. Here is the form instead.',
    may_not_decide:
        'You may not decide approvals in this company, and the COO would ask you to. Here is the form instead.',
    failed: 'Could not start a conversation to create a task. Here is the form instead.',
}

/** The refusal the API opens a purpose conversation with when nobody holds the COO role. */
const NO_COO = 'no_coo'

/** How often the conversation is read again while the COO is replying. */
const REPLY_POLL_MS = 1000

/** The entry key of a message on its way, drawn at the end of the thread the moment it is sent. */
const OUTGOING_KEY = 'create-task-outgoing'

/**
 * Whether `error` is the API's `no_coo` refusal: the code `request` carries (`refusal`), or one named on the error
 * itself (`code`, `error`).
 */
export function isNoCooRefusal(error: unknown): boolean {
    if (refusalCode(error) === NO_COO) {
        return true
    }
    const named = (error ?? {}) as { code?: unknown; error?: unknown }
    return named.code === NO_COO || named.error === NO_COO
}

/**
 * What stops the conversation before it is opened, or null when nothing does: the AI account read as not connected,
 * then the person's grant. A grant or an account the API did not report stops nothing: the page then tries the
 * conversation, and falls back if it will not open.
 */
export function refusedBefore(grant: Me | null, account: AiAccountStatus): CreateTaskFallback | null {
    if (account === 'not_connected') {
        return 'no_ai_account'
    }
    if (!grantWrites(grant, 'chats')) {
        return 'may_not_chat'
    }
    if (!grantWrites(grant, 'gates')) {
        return 'may_not_decide'
    }
    return null
}

export type CreateTaskConversation = {
    /** `idle` until asked to open, `checking` while it opens, then `open` or `fallback`. */
    state: Ref<'idle' | 'checking' | 'open' | 'fallback'>
    fallback: Ref<CreateTaskFallback | null>
    conversation: Ref<ChatDetail | null>
    entries: ComputedRef<ChatEntry[]>
    thinking: ComputedRef<boolean>
    draft: Ref<string>
    canSend: ComputedRef<boolean>
    error: Ref<string | null>
    restarting: Ref<boolean>
    /**
     * A message or a tool-call decision is still on its way: it belongs to the conversation it was made in, so Start
     * over waits for it rather than carrying it onto a fresh one.
     */
    busy: ComputedRef<boolean>
    toolsByKey: ComputedRef<Map<string, ChatMessageRow[]>>
    toolItems: (tools: ChatMessageRow[]) => ChatToolItem[]
    /** The tool log under one entry of the thread, as the shared conversation draws it. */
    toolLog: (entryId: string) => ChatToolItem[]
    /** The tool calls drawn as cards at the end of the thread: waiting on the person, or just decided. */
    waitingCalls: ComputedRef<ChatMessageRow[]>
    callDecided: (row: ChatMessageRow) => GateDecision | undefined
    decidingCallId: Ref<string | null>
    start: () => Promise<void>
    send: () => Promise<void>
    startOver: () => Promise<void>
    /** Allows once, always allows or denies a tool call waiting on the person, as the Chat page does. */
    decideCall: (row: ChatMessageRow, decision: ChatToolCallDecision) => Promise<void>
}

/**
 * Create task's conversation with the COO, in Standard (#244). `start` checks what would stop it, then opens the
 * create-a-task conversation: the server answers with the person's unfinished one from the last day, or a new one
 * with only the greeting, and the page shows exactly that. A sent message shows at once, then the COO thinking, then
 * the reply; the conversation is read again every second while a reply is under way. Start over asks for a fresh one.
 * The account's mode is never written here.
 */
export function useCreateTaskConversation(): CreateTaskConversation {
    const { mode } = useUiMode()
    const state = ref<'idle' | 'checking' | 'open' | 'fallback'>('idle')
    const fallback = ref<CreateTaskFallback | null>(null)
    const conversation = ref<ChatDetail | null>(null)
    const draft = ref('')
    const outgoing = ref<string | null>(null)
    const error = ref<string | null>(null)
    const restarting = ref(false)
    let timer: ReturnType<typeof setTimeout> | null = null
    let gone = false

    /** The tool calls waiting on the person, as the Chat page holds them (the same cards, the same decisions). */
    const { decidingCallId, waitingCalls, onCard, callDecided, canDecide, decide } = useChatToolCalls(
        () => conversation.value?.messages ?? [],
    )

    /** The thread, without the calls drawn as cards: those wait at its end, as on the Chat page. */
    const rows = computed(() => threadEntries((conversation.value?.messages ?? []).filter(row => !onCard(row))))

    /** The thread as the shared conversation draws it, with the message on its way at the end. */
    const entries = computed((): ChatEntry[] => [
        ...rows.value.map((entry): ChatEntry => {
            if (entry.kind === 'tools') {
                return { kind: 'assistant', id: entry.key, text: '' }
            }
            const text = entry.row.content ?? ''
            return entry.row.role === 'user'
                ? { kind: 'user', id: entry.key, text, sender: entry.row.sender ?? null }
                : { kind: 'assistant', id: entry.key, text }
        }),
        ...(outgoing.value === null ? [] : [{ kind: 'user', id: OUTGOING_KEY, text: outgoing.value } as ChatEntry]),
    ])

    const thinking = computed(
        () => outgoing.value !== null || decidingCallId.value !== null || conversation.value?.status === 'replying',
    )

    /** Never into a closed conversation, while a message is on its way, or while a tool call is being decided. */
    const canSend = computed(
        () =>
            conversation.value !== null &&
            conversation.value.status !== 'closed' &&
            outgoing.value === null &&
            decidingCallId.value === null &&
            draft.value.trim() !== '',
    )

    const toolsByKey = computed(() => new Map(rows.value.map(entry => [entry.key, entry.tools])))

    const busy = computed(() => outgoing.value !== null || decidingCallId.value !== null)

    /** The tool rows behind one reply, as the Chat page's log names them in the current mode. */
    function toolItems(tools: ChatMessageRow[]): ChatToolItem[] {
        return tools.map(row => ({
            id: row.id,
            tool: row.toolName ? toolActionLabel(row.toolName, mode.value) : 'Tool update',
            runId: toolRunId(row),
            slug: typeof row.payload?.slug === 'string' ? row.payload.slug : null,
            status: null,
            resources: [],
            payload: toolPayload(row),
        }))
    }

    function toolLog(entryId: string): ChatToolItem[] {
        return toolItems(toolsByKey.value.get(entryId) ?? [])
    }

    function stopFollowing(): void {
        if (timer !== null) {
            clearTimeout(timer)
            timer = null
        }
    }

    /** Shows `chat`, and reads it again in a second while the COO is still replying in it. */
    function show(chat: ChatDetail): void {
        conversation.value = chat
        state.value = 'open'
        stopFollowing()
        if (chat.status === 'replying' && !gone) {
            timer = setTimeout(() => void reread(chat.id), REPLY_POLL_MS)
        }
    }

    async function reread(id: string): Promise<void> {
        timer = null
        let chat: ChatDetail
        try {
            chat = await api.chat(id)
        } catch {
            // A read that failed is tried again: the reply is still under way as far as the page knows.
            if (!gone && conversation.value?.id === id) {
                timer = setTimeout(() => void reread(id), REPLY_POLL_MS)
            }
            return
        }
        if (!gone && conversation.value?.id === id) {
            show(chat)
        }
    }

    function fallBack(reason: CreateTaskFallback): void {
        fallback.value = reason
        state.value = 'fallback'
    }

    async function start(): Promise<void> {
        if (state.value !== 'idle') {
            return
        }
        state.value = 'checking'
        const [grant, account] = await Promise.all([api.me().catch(() => null), readAiAccountStatus()])
        if (gone) {
            return
        }
        const refused = refusedBefore(grant, account)
        if (refused) {
            fallBack(refused)
            return
        }
        try {
            const chat = await api.openChatForPurpose(CREATE_TASK_CHAT_PURPOSE)
            if (!gone) {
                show(chat)
            }
        } catch (caught) {
            fallBack(isNoCooRefusal(caught) ? 'no_coo' : 'failed')
        }
    }

    async function send(): Promise<void> {
        const chat = conversation.value
        const text = draft.value.trim()
        if (!chat || !canSend.value) {
            return
        }
        outgoing.value = text
        draft.value = ''
        error.value = null
        try {
            if (!(await requireAiAccount())) {
                draft.value = text
                return
            }
            const answered = await api.postChatMessage(chat.id, text)
            if (!gone && conversation.value?.id === chat.id) {
                show(answered)
            }
        } catch {
            draft.value = text
            error.value = 'Your message was not sent. Try again.'
        } finally {
            outgoing.value = null
        }
    }

    async function startOver(): Promise<void> {
        // A message or a tool-call decision still on its way belongs to the conversation it was made in: Start over
        // waits for it, so none of it is drawn on (or blocks) the fresh one.
        if (restarting.value || busy.value) {
            return
        }
        restarting.value = true
        error.value = null
        try {
            const chat = await api.openChatForPurpose(CREATE_TASK_CHAT_PURPOSE, { fresh: true })
            if (!gone) {
                draft.value = ''
                show(chat)
            }
        } catch {
            error.value = 'Could not start over. Try again.'
        } finally {
            restarting.value = false
        }
    }

    /**
     * Allows once, always allows or denies a tool call the COO waits on, on its card here, as the Chat page does
     * (useChatToolCalls): the card shows the decision at once and goes; the API's answer carries the COO's turn on,
     * and is shown (and followed while the COO is still replying). A refusal brings the card back with the Chat page's
     * reason, after reading the conversation again when the call has an outcome already.
     */
    async function decideCall(row: ChatMessageRow, decision: ChatToolCallDecision): Promise<void> {
        const chat = conversation.value
        if (!chat || !canDecide(row)) {
            return
        }
        // #1796: the COO's turn carries on from the decision, whichever it is: model work, so it asks the guard first.
        if (!(await requireAiAccount())) {
            return
        }
        error.value = null
        const record = (callId: string) => api.decideChatToolCall(chat.id, callId, decision)
        const outcome = await decide(row, decision, record, decided => {
            if (!gone && conversation.value?.id === chat.id) {
                show(decided)
            }
        })
        if (!outcome || outcome.recorded || gone) {
            return
        }
        const refused = decisionRefusal(outcome.refusal, memberLabel(chat.member))
        if (refused.reread) {
            try {
                const read = await api.chat(chat.id)
                if (!gone && conversation.value?.id === chat.id) {
                    show(read)
                }
            } catch {
                // The reason still stands without the read: the card is back as it was.
            }
        }
        error.value = refused.message
    }

    onUnmounted(() => {
        gone = true
        stopFollowing()
    })

    return {
        state,
        fallback,
        conversation,
        entries,
        thinking,
        draft,
        canSend,
        error,
        restarting,
        busy,
        toolsByKey,
        toolItems,
        toolLog,
        waitingCalls,
        callDecided,
        decidingCallId,
        start,
        send,
        startOver,
        decideCall,
    }
}
